import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// Hook: Check session on mount and redirect if invalid
export const SessionCheck = (): boolean => {
  const navigate = useNavigate();
  const isSessionValid: boolean = localStorage.getItem("session_uid") !== null;

  useEffect(() => {
    if (!isSessionValid) {
      navigate("/");
    }
  }, [isSessionValid, navigate]);

  return isSessionValid;
};

// Convert HH:mm:ss string to seconds
const timeToSeconds = (time: string): number => {
  const [hours = 0, minutes = 0, seconds = 0] = time?.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

// Extend session expiry in localStorage
export const SessionExtend = (): void => {
  const timeoutStr = localStorage.getItem("session_timeout");

  if (timeoutStr) {
    const session_timeout = timeToSeconds(timeoutStr);

    if (session_timeout > 0) {
      const currentMoment = moment.utc();
      const new_expiry = currentMoment.add(session_timeout, "seconds");
      localStorage.setItem("expiry_datetime", new_expiry.toISOString());
    } else {
      console.error(
        "Invalid session_timeout. It must be a positive time string."
      );
    }
  } else {
    console.error("session_timeout is missing from localStorage.");
  }
};

// Check session expiry and navigate if expired
export const CheckSessionExpiry = (navigateToHome: () => void): void => {
  const session_expiry = localStorage.getItem("expiry_datetime");

  if (session_expiry) {
    const currentUtcTime = moment.utc();
    const sessionExpiryTime = moment.utc(session_expiry);

    if (currentUtcTime.isAfter(sessionExpiryTime)) {
      console.log("Session expired. Redirecting to home page...");
      navigateToHome();
    } else {
      console.log("Session is still valid.");
    }
  } else {
    console.error("Session expiry time is missing in localStorage.");
    navigateToHome();
  }
};

// Hook: Verify session presence and redirect
export const useSessionCheck = (): void => {
  const navigate = useNavigate();

  useEffect(() => {
    const sessionValid = localStorage.getItem("session_uid") !== null;
    if (!sessionValid) {
      navigate("/");
    }
  }, [navigate]);
};

// Hook: Re-run a function at an interval
export const usePageRefresh = (
  intervalInSeconds: number,
  refreshFunction: () => void
): void => {
  useEffect(() => {
    if (!intervalInSeconds || typeof refreshFunction !== "function") return;

    const intervalInMilliseconds = intervalInSeconds * 1000;

    const intervalId = setInterval(() => {
      refreshFunction();
    }, intervalInMilliseconds);

    return () => clearInterval(intervalId);
  }, [intervalInSeconds, refreshFunction]);
};

// Hook: Save session on window unload
export const useSaveSessionOnUnload = (
  userUid: string,
  sessionUid: string
): void => {
  useEffect(() => {
    const handleBeforeUnload = (): void => {
      localStorage.setItem("user_uid", userUid);
      localStorage.setItem("session_uid", sessionUid);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [userUid, sessionUid]);
};

// Duplicate of above, can be removed or kept if needed separately
export const useSaveSessionOnClose = (
  userUid: string,
  sessionUid: string
): void => {
  useEffect(() => {
    const handleBeforeUnload = (): void => {
      localStorage.setItem("user_uid", userUid);
      localStorage.setItem("session_uid", sessionUid);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [userUid, sessionUid]);
};

// Utility: Check if both session and user exist
export const checkAndVerifySession = (): boolean => {
  const sessionUid = localStorage.getItem("session_uid");
  const userUid = localStorage.getItem("user_uid");
  return !!(sessionUid && userUid);
};
